// Site navigation: hover and keyboard dropdowns, condensed bar on scroll,
// active section highlighting on the home page and the mobile sheet.

import { initTheme } from './theme.js';
import { initLanguage } from './language.js';
import { t } from '../i18n/index.js';
initLanguage();
initTheme();

const header = document.querySelector('[data-nav]');

if (header) {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const html = document.documentElement;

    // Dropdowns -------------------------------------------------------------
    const dropdowns = [...header.querySelectorAll('[data-nav-dropdown]')].map(root => ({
        root,
        trigger: root.querySelector('[data-nav-trigger]'),
        panel: root.querySelector('[data-nav-panel]'),
        pinned: false,
        closeTimer: 0,
        openTimer: 0,
    }));

    const isOpen = dropdown => dropdown.root.dataset.open === 'true';

    function close(dropdown) {
        window.clearTimeout(dropdown.closeTimer);
        window.clearTimeout(dropdown.openTimer);
        dropdown.pinned = false;
        dropdown.root.dataset.open = 'false';
        dropdown.panel.dataset.open = 'false';
        dropdown.trigger.setAttribute('aria-expanded', 'false');
        dropdown.panel.inert = true;
    }

    function open(dropdown, pinned = false) {
        if (!desktop.matches) return;
        dropdowns.filter(other => other !== dropdown).forEach(close);
        window.clearTimeout(dropdown.closeTimer);
        dropdown.pinned ||= pinned;
        dropdown.root.dataset.open = 'true';
        dropdown.panel.dataset.open = 'true';
        dropdown.trigger.setAttribute('aria-expanded', 'true');
        dropdown.panel.inert = false;
        header.classList.remove('is-hidden');
    }

    const links = dropdown => [...dropdown.panel.querySelectorAll('a[href]')];

    // A visibility transition can update styles before contents can receive
    // focus. Wait for actual focus, without taking it from a user already inside.
    function focusAfterOpen(panel, target, stillOpen, options = {}) {
        if (!target) return;
        const focus = () => {
            if (!stillOpen() || panel.contains(document.activeElement)) return;
            target.focus(options);
            if (!panel.contains(document.activeElement)) requestAnimationFrame(focus);
        };
        requestAnimationFrame(focus);
    }

    dropdowns.forEach(dropdown => {
        dropdown.root.addEventListener('pointerenter', event => {
            if (event.pointerType !== 'mouse') return;
            window.clearTimeout(dropdown.closeTimer);
            if (isOpen(dropdown)) return;
            // Hover intent: switching from another open menu waits a moment, so a
            // diagonal path into a wide panel does not flip to the neighbouring menu.
            const switching = dropdowns.some(other => other !== dropdown && isOpen(other));
            window.clearTimeout(dropdown.openTimer);
            dropdown.openTimer = window.setTimeout(() => open(dropdown), switching ? 180 : 60);
        });
        dropdown.root.addEventListener('pointerleave', () => {
            window.clearTimeout(dropdown.openTimer);
            if (dropdown.pinned || dropdown.root.contains(document.activeElement)) return;
            // A short grace period lets the pointer travel from the trigger into the panel.
            dropdown.closeTimer = window.setTimeout(() => close(dropdown), 220);
        });
        dropdown.trigger.addEventListener('click', () => {
            if (isOpen(dropdown) && dropdown.pinned) close(dropdown);
            else open(dropdown, true);
        });
        dropdown.trigger.addEventListener('keydown', event => {
            if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
            event.preventDefault();
            open(dropdown, true);
            const items = links(dropdown);
            focusAfterOpen(dropdown.panel, event.key === 'ArrowDown' ? items[0] : items.at(-1), () => isOpen(dropdown));
        });
        dropdown.panel.addEventListener('keydown', event => {
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const items = links(dropdown);
            let index = items.indexOf(document.activeElement);
            if (event.key === 'Home') index = 0;
            else if (event.key === 'End') index = items.length - 1;
            else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
            items[index]?.focus();
        });
        dropdown.root.addEventListener('focusout', event => {
            if (!dropdown.root.contains(event.relatedTarget)) close(dropdown);
        });
        dropdown.panel.addEventListener('click', event => {
            if (event.target.closest('a')) close(dropdown);
        });
    });

    document.addEventListener('pointerdown', event => {
        dropdowns.forEach(dropdown => { if (!dropdown.root.contains(event.target)) close(dropdown); });
    });

    // Mobile sheet ------------------------------------------------------------
    const burger = header.querySelector('[data-nav-burger]');
    const sheet = header.querySelector('[data-nav-mobile]');

    function setSheet(openSheet) {
        if (!burger || !sheet) return;
        header.classList.toggle('is-open', openSheet);
        html.classList.toggle('an-nav-locked', openSheet);
        burger.setAttribute('aria-expanded', String(openSheet));
        burger.setAttribute('aria-label', t(openSheet ? 'navigation.closeMenu' : 'navigation.openMenu'));
        sheet.inert = !openSheet;
        if (openSheet) {
            header.classList.remove('is-hidden');
            focusAfterOpen(sheet, sheet.querySelector('button, a'), () => header.classList.contains('is-open'), { preventScroll: true });
        }
    }

    burger?.addEventListener('click', () => setSheet(!header.classList.contains('is-open')));
    window.addEventListener('an:languagechange', () => burger?.setAttribute('aria-label', t(header.classList.contains('is-open') ? 'navigation.closeMenu' : 'navigation.openMenu')));
    sheet?.addEventListener('click', event => { if (event.target.closest('a[href]')) setSheet(false); });
    sheet?.querySelectorAll('.an-mobile__top[aria-controls]').forEach(button => {
        button.addEventListener('click', () => {
            button.setAttribute('aria-expanded', String(button.getAttribute('aria-expanded') !== 'true'));
        });
    });

    document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        if (header.classList.contains('is-open')) {
            event.preventDefault();
            setSheet(false);
            burger?.focus();
            return;
        }
        const current = dropdowns.find(isOpen);
        if (!current) return;
        event.preventDefault();
        close(current);
        current.trigger.focus();
    });

    desktop.addEventListener('change', () => {
        dropdowns.forEach(close);
        if (desktop.matches) setSheet(false);
    });

    // Condensed bar on scroll, tucked away while reading downwards -----------------
    let lastY = null;
    let frame = 0;
    function onScroll() {
        frame = 0;
        const y = window.scrollY;
        if (lastY === null) lastY = y;
        header.classList.toggle('is-scrolled', y > 24);
        const busy = header.classList.contains('is-open') || dropdowns.some(isOpen) || header.contains(document.activeElement);
        if (y <= 640 || y < lastY - 4) header.classList.remove('is-hidden');
        else if (!busy && y > lastY + 4) header.classList.add('is-hidden');
        if (Math.abs(y - lastY) > 4) lastY = y;
    }
    window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(onScroll); }, { passive: true });
    // Let initial locale/theme DOM changes settle before reading scroll geometry.
    frame = requestAnimationFrame(onScroll);

    // Highlight the section in view on the home page ---------------------------------
    const sectionLinks = [...header.querySelectorAll('[data-nav-section]')];
    const sections = sectionLinks.map(link => document.getElementById(link.dataset.navSection)).filter(Boolean);
    if (sections.length && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const link = sectionLinks.find(item => item.dataset.navSection === entry.target.id);
                if (!link) return;
                if (entry.isIntersecting) link.setAttribute('aria-current', 'true');
                else link.removeAttribute('aria-current');
            });
        }, { rootMargin: '-45% 0px -50% 0px' });
        sections.forEach(section => observer.observe(section));
    }
}
